import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, daysAgo, today } from "@/lib/api";
import { Btn, DataTable, Field, Select } from "@/components/shared";
import { Chip, money, call, when, Modal, MemberPicker, ExportBtn } from "./common";

function RecoverDialog({ c, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setAmount(c ? String(c.outstanding) : ""), [c]);
  const go = async () => {
    setBusy(true);
    const r = await call(() => api.post(`/admin/recon/credits/${c.credit_id}/recover`, { amount: Number(amount) }));
    setBusy(false);
    if (!r) return;
    if (r.status === "SUCCESS") toast.success(`Recovered ${money(Number(amount))}`); else toast.error(`Recovery failed: ${r.reason}. Logged as failed attempt.`);
    onDone();
  };
  return (
    <Modal open={!!c} onClose={onClose} title={`Recover · ${c?.credit_id || ""}`} testid="recover-dialog">
      {c && (
        <div className="space-y-3">
          <div className="text-sm">{c.login_id} · {c.name} — outstanding <b className="font-mono">{money(c.outstanding)}</b></div>
          <Field label="Amount to debit from merchant main wallet"><input type="number" className="np-input font-mono" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="recover-amount" /></Field>
          <div className="text-[11px] text-slate-500">Outstanding goes down only when the wallet debit posts. If the balance is short, the attempt is saved as failed — no negative balance is created. Enter a smaller amount for partial recovery.</div>
          <Btn onClick={go} loading={busy} disabled={!(Number(amount) > 0)} data-testid="recover-submit">Recover</Btn>
        </div>
      )}
    </Modal>
  );
}

export function CreditRecovery() {
  const [f, setF] = useState({ status: "", q: "", date_from: daysAgo(90), date_to: today() });
  const [member, setMember] = useState(null);
  const [rows, setRows] = useState([]);
  const [rec, setRec] = useState(null);
  const [hist, setHist] = useState(null);
  const load = () => api.get("/admin/recon/credits", { params: { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), member: member?.id } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cols = [
    { label: "Credit ID", key: "credit_id", render: (r) => <span className="font-mono text-xs">{r.credit_id}</span> },
    { label: "Merchant", key: "login_id", render: (r) => <span className="text-xs"><b className="font-mono">{r.login_id}</b> {r.name}</span> },
    { label: "Issued", key: "amount", render: (r) => <span className="font-mono">{money(r.amount)}</span> },
    { label: "Recovered", key: "recovered", render: (r) => <span className="font-mono text-emerald-700">{money(r.recovered)}</span> },
    { label: "Outstanding", key: "outstanding", render: (r) => <span className="font-mono font-bold" data-testid={`credit-outstanding-${r.credit_id}`}>{money(r.outstanding)}</span> },
    { label: "Issued at", key: "issued_at", render: (r) => <span className="text-xs">{when(r.issued_at)}</span> },
    { label: "Due", key: "due_at", render: (r) => <span className="text-xs">{when(r.due_at)}</span> },
    { label: "Ageing", key: "ageing_hours", render: (r) => <span className="text-xs">{r.ageing_hours}h</span> },
    { label: "Status", key: "display_status", render: (r) => <Chip s={r.display_status} testid={`credit-status-${r.credit_id}`} /> },
    { label: "History", csv: (r) => r.recoveries.map((x) => `${x.status} ${x.amount}`).join("; "), render: (r) => <button className="text-xs text-blue-700" onClick={() => setHist(r)} data-testid={`credit-history-${r.credit_id}`}>{r.recoveries.length} attempt(s)</button> },
    { label: "Action", render: (r) => r.outstanding > 0 && <button className="text-xs font-semibold text-blue-700" onClick={() => setRec(r)} data-testid={`credit-recover-${r.credit_id}`}>Recover</button> },
  ];
  return (
    <div>
      <div className="np-card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status"><Select options={[["", "All"], ["OPEN", "Open"], ["PARTIAL", "Partially recovered"], ["OVERDUE", "Overdue"], ["RECOVERED", "Recovered"]].map(([value, label]) => ({ value, label }))} value={f.status} onChange={set("status")} data-testid="credit-status-filter" /></Field>
        <MemberPicker value={member} onChange={setMember} testid="credit-member" />
        <Field label="Reference"><input className="np-input w-40" value={f.q} onChange={set("q")} data-testid="credit-q" /></Field>
        <Field label="From"><input type="date" className="np-input" value={f.date_from} onChange={set("date_from")} /></Field>
        <Field label="To"><input type="date" className="np-input" value={f.date_to} onChange={set("date_to")} /></Field>
        <Btn onClick={load} data-testid="credit-search">Search</Btn>
        <div className="ml-auto"><ExportBtn name="credit-recovery" rows={rows} cols={cols} /></div>
      </div>
      <p className="mb-3 text-xs text-slate-500">Fund requests against the company bank marked <b>Credit</b> become recoverable credit, due 12 hours after approval. Recovery is manual and debits only the borrower&apos;s own wallet.</p>
      <DataTable testid="credits-table" rows={rows} columns={cols} empty="No credits issued" />
      <RecoverDialog c={rec} onClose={() => setRec(null)} onDone={() => { setRec(null); load(); }} />
      <Modal open={!!hist} onClose={() => setHist(null)} title={`Recovery history · ${hist?.credit_id || ""}`} testid="credit-history-dialog">
        <DataTable rows={hist?.recoveries || []} columns={[
          { label: "#", key: "n" }, { label: "Amount", render: (r) => money(r.amount) }, { label: "Status", render: (r) => <Chip s={r.status} /> },
          { label: "Reason", render: (r) => r.reason || "—" }, { label: "By", key: "by" }, { label: "At", render: (r) => when(r.at) },
        ]} />
      </Modal>
    </div>
  );
}
